import {useDispatch} from 'react-redux';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  GetMetadaServiceCaseApiResponse,
  GetPendingServiceItemsApiResponse,
  GetPendingServiceOrderReturnApiResponse,
  metadaWorkshopServiceOrderApi,
} from '@dms/api/workshop';
import {queryParams, logError, refreshDmsDatagrid} from '@dms/shared';

import {useQueryState} from 'shared/web';

import {refreshServiceOrderGrids} from '../utils/refreshServiceOrderGrids';

interface UseServiceCaseDetailHandlersProps {
  pendingServiceItems?: GetPendingServiceItemsApiResponse;
  pendingServiceOrderReturn?: GetPendingServiceOrderReturnApiResponse;
  serviceCase?: GetMetadaServiceCaseApiResponse;
}

export function useServiceCaseDetailHandlers(props: UseServiceCaseDetailHandlersProps) {
  const dispatch = useDispatch();
  const [, setOrderId] = useQueryState(queryParams.SERVICE_CASE_ORDER_ID);
  const [, setAddLabourJobId] = useQueryState(queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID);
  const [, setAddMaterialRequestJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_MATERIAL_REQUEST_JOB_ID
  );
  const [, setServiceOrderReturnId, clearServiceOrderReturnId] = useQueryState(
    queryParams.SERVICE_ORDER_RETURN_ID
  );
  const [
    ,
    setServiceOrderReturnAuthorizationProfileId,
    clearServiceOrderReturnAuthorizationProfileId,
  ] = useQueryState(queryParams.SERVICE_ORDER_RETURN_AUTHORIZATION_PROFILE_ID);

  const handlePendingLabourClick = () => {
    const labourBasket = props.pendingServiceItems?.workBasket;
    if (!isNotNilOrEmpty(labourBasket)) {
      return;
    }

    setAddLabourJobId(labourBasket?.[0]?.serviceJobId ?? '');
    setOrderId(labourBasket?.[0]?.serviceOrderId ?? '');
  };

  const handlePendingMaterialClick = () => {
    const materialBasket = props.pendingServiceItems?.materialBasket;
    if (!isNotNilOrEmpty(materialBasket)) {
      return;
    }

    setAddMaterialRequestJobId(materialBasket?.[0]?.serviceJobId ?? '');
    setOrderId(materialBasket?.[0]?.serviceOrderId ?? '');
  };

  const handlePendingReturnClick = () => {
    const returnId = props.pendingServiceOrderReturn?.serviceOrderReturnId;
    const authProfileId = props.serviceCase?.authorizationProfileId;

    if (isNil(returnId)) {
      return logError('serviceOrderReturnId is not defined');
    }
    if (isNil(authProfileId)) {
      return logError('authorizationProfileId is not defined');
    }

    setServiceOrderReturnId(returnId);
    setServiceOrderReturnAuthorizationProfileId(authProfileId);
  };

  const handleServiceOrderReturnSubmit = () => {
    clearServiceOrderReturnId();
    clearServiceOrderReturnAuthorizationProfileId();
    // A pending return can belong to an order other than the one selected in the URL.
    dispatch(metadaWorkshopServiceOrderApi.util.invalidateTags(['partMovementsCount']));
    refreshServiceOrderGrids();
    refreshDmsDatagrid('service-case-order-items-returned');
  };

  return {
    handlePendingLabourClick,
    handlePendingMaterialClick,
    handlePendingReturnClick,
    handleServiceOrderReturnSubmit,
  };
}
